import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { DBAccount, DBPosition } from "../../../api/types";
import {
  Checkbox,
  FormControlLabel,
  Box,
  Paper,
  Grid,
  Divider,
  Tabs,
  Tab,
  Button,
  Typography,
} from "@mui/material";
import {
  calculateHoldingsPerCoin,
  dollar,
  multiplyOrZero,
  shortenWallet,
  smartRound,
} from "../../../utils/utils";
import { SimpleDataTable } from "../../../components/SimpleDataTable";
import Position from "./positionsDetails";
import AccountBalanceGraph from "./graph/balanceGraph";
import AccountProfitGraph from "./graph/profitGraph";
import AlgorithmBalanceGraph from "./graph/algorithmBalanceGraph";
import ExpandableDataTable from "../../../components/ExpandableDataTable";
import ChooseLogModal from "./logs-modal/overview-modal/LogsOverviewModal";
import { useSimulatedLoginContext } from "../../../context/globalDataContext";
import { BASE_VALUE } from "../../../constants";
import { usePositionsContext } from "../../../context/positionsContext";
import { PositionsFilter } from "./positionsFilter";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { disguiseAddress, useDisguise } from "../../../context/disguiseContext";
import ArchiveAccountModal from "./logs-modal/archive-modal/ArchiveAccountModal";
import { SettingChips } from "./setting-chips";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AccountStatistics from "../../../components/AccountStatistics";
import ProprAccountModal from "./propr-modal/ProprAccountModal";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import OwnerModal from "./owner-modal/OwnerModal";

export default function AccountOverview({
  account,
  reloadAccounts,
  owners,
}: {
  account: DBAccount;
  reloadAccounts: () => void;
  owners: string[];
}) {
  const {
    setAccountId,
    positions,
    filteredPositions: timeFilteredPositions,
    rangeStartTs,
    hasTradesBeforeRange,
  } = usePositionsContext();
  const [showCancelled, setShowCancelled] = useState(false);
  const [showSimulated, setShowSimulated] = useState(false);
  const [showAllPositions, setShowAllPositions] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [proprModalOpen, setProprModalOpen] = useState(false);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const { enabled: disguise } = useDisguise();
  const displayAddress = disguiseAddress(account.hyperliquidAddress, disguise);
  const isPropr = !!account.proprAccountId;

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setAccountId(account._id.toString());
  }, [account._id, setAccountId]);

  // --- Helper functions for holdings logic ---
  const matchesHoldingCoin = (position: DBPosition, coin?: string) => {
    if (!coin) return false;
    return (position.symbol || "").toUpperCase().includes(coin.toUpperCase());
  };

  const getOpenPositionsForCoin = (coin?: string) => {
    if (!coin) return [] as DBPosition[];
    return positions.filter(
      (p) => matchesHoldingCoin(p, coin) && p.status === "OPEN",
    );
  };

  const sumOpenPositionsUsd = (positionsArr: DBPosition[]) => {
    return positionsArr.reduce((sum, p) => {
      const price = p.openTransaction?.price ?? 0;
      const coinAmount = p.openTransaction?.coinAmount ?? p.coinAmount ?? 0;
      return sum + multiplyOrZero(coinAmount, price);
    }, 0);
  };

  const buildHoldingTableData = (
    holding: {
      usdValue: number;
      nativeValue: number;
      coin: string;
    },
    holdingsPerCoin: { [key: string]: number },
  ) => {
    const positionsForCoin = getOpenPositionsForCoin(holding.coin);
    const openPositionsUsdTotal = sumOpenPositionsUsd(positionsForCoin);
    const positionHolding = dollar(
      holdingsPerCoin[holding.coin + BASE_VALUE] ?? 0,
    );

    const usdVal = dollar(holding.usdValue ?? 0);

    return {
      key: holding.coin,
      sort: Math.abs(holding.usdValue),
      title: `${holding.coin} - ${usdVal} [${positionHolding}]`,
      tableData: {
        "USD value (holding)": usdVal,
        "Holdings according to positions": positionHolding,
        "Native value": smartRound(holding.nativeValue ?? 0),
        "Open positions (USD total)": dollar(openPositionsUsdTotal),
      } as { [key: string]: string | number },
    };
  };

  const { tickerLookup } = useSimulatedLoginContext();
  const holdingsPerCoin = useMemo(
    () => calculateHoldingsPerCoin(positions, tickerLookup),
    [positions, tickerLookup],
  );

  // Precompute holdings data to keep render clean
  const holdingsTableData = (account.currentHoldings ?? [])
    .map((h) => buildHoldingTableData(h, holdingsPerCoin))
    .sort((a, b) => b.sort - a.sort);

  const nonCanceledPositions = positions.filter(
    (position) => position.status !== "CANCELLED",
  );

  const filteredPositions = showCancelled ? positions : nonCanceledPositions;

  // Calculate summary values
  const openPositions = positions.filter((p) => p.status === "OPEN");
  const openPositionsCoinSum = openPositions.reduce(
    (sum, p) =>
      sum +
      multiplyOrZero(p?.openTransaction?.coinAmount, p?.openTransaction?.price),
    0,
  );

  const totalEquity = account.totalEquity;
  const usdBalance = account.usdBalance;

  // OKX/Propr accounts have no wallet address, so skip the wallet row
  const hasWallet = !!account.hyperliquidAddress;
  const topFullRows = isMobile || !hasWallet ? [] : [displayAddress];
  const algorithmName =
    /TradeExecute[^\s]+\s(.+?).csv/gi.exec(account.settings.buyCsvFile)?.[1] ??
    "UNKNOWN";

  const summaryDataTable = {
    ...(isMobile && hasWallet
      ? {
          Wallet: disguise
            ? displayAddress
            : shortenWallet(account.hyperliquidAddress),
        }
      : {}),
    "Total equity": dollar(totalEquity),
    Algorithm: algorithmName,
    "USD Balance": dollar(usdBalance),
    "Sum Open positions": dollar(openPositionsCoinSum),
  };

  const otherPositions = (
    showCancelled ? positions : nonCanceledPositions
  ).filter((p) => p.status !== "OPEN");

  const positionsToRender = showAllPositions
    ? otherPositions
    : otherPositions.slice(0, 25);

  return (
    <div>
      <Divider />
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <SimpleDataTable
              topFullRows={topFullRows}
              tableData={summaryDataTable}
              options={{
                copyAllDefault: true,
                copy: {
                  Wallet: displayAddress,
                  Algorithm: algorithmName,
                },
              }}
            />
          </Paper>
          <ChooseLogModal
            handleClose={() => setLogsModalOpen(false)}
            open={logsModalOpen}
          />
          <ArchiveAccountModal
            reloadAccounts={reloadAccounts}
            handleClose={() => setArchiveModalOpen(false)}
            open={archiveModalOpen}
            account={account}
          />
          <OwnerModal
            reloadAccounts={reloadAccounts}
            handleClose={() => setOwnerModalOpen(false)}
            open={ownerModalOpen}
            account={account}
            owners={owners}
          />
          {isPropr && (
            <ProprAccountModal
              reloadAccounts={reloadAccounts}
              handleClose={() => setProprModalOpen(false)}
              open={proprModalOpen}
              account={account}
            />
          )}

          <Box sx={{ mb: 2 }}>
            <SettingChips account={account} />
          </Box>

          <Grid container columnSpacing={1}>
            <Button variant="outlined" onClick={() => setLogsModalOpen(true)}>
              <StickyNote2Icon />
              &nbsp;Logs
            </Button>
            <Button variant="outlined" onClick={() => setOwnerModalOpen(true)}>
              <PersonIcon />
              &nbsp;{account.owner ?? "Owner"}
            </Button>
            {isPropr && (
              <Button
                variant="outlined"
                onClick={() => setProprModalOpen(true)}
              >
                <SettingsIcon />
                &nbsp;Propr
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setArchiveModalOpen(true)}
              color="warning"
            >
              Archive
            </Button>
          </Grid>
          <Box sx={{ marginLeft: "auto" }}></Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <h3 style={{ flex: 1 }}>Holdings</h3>
            <a
              target="_blank"
              href={"trade-inspection?name=" + account.name}
              rel="noopener noreferrer"
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {/* <p > */}
              Inspect trades
              <OpenInNewIcon />
              {/* </p> */}
            </a>
          </Box>

          {/* Render an ExpandableDataTable for each precomputed holding table */}
          {holdingsTableData && holdingsTableData.length > 0 ? (
            holdingsTableData.map((h) => (
              <ExpandableDataTable
                key={h.key}
                defaultOpen={false}
                title={h.title}
                tableData={h.tableData}
              />
            ))
          ) : (
            <Typography color="grey" style={{ fontStyle: "italic" }}>
              No holdings
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              columnGap: 2,
              mb: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="basic tabs example"
            >
              <Tab label="Balance graph" />
              <Tab label="Profit graph" />
              <Tab label="Algorithm graph" />
            </Tabs>
            <Box sx={{ py: 1 }}>
              <PositionsFilter />
            </Box>
          </Box>
          {tabValue !== 2 && (
            <Box sx={{ mb: 2 }}>
              <AccountStatistics positions={timeFilteredPositions} />
            </Box>
          )}
          {tabValue === 0 && (
            <AccountBalanceGraph
              account={account}
              positions={timeFilteredPositions}
              rangeStartTs={rangeStartTs}
              extendToRangeStart={hasTradesBeforeRange}
            />
          )}
          {tabValue === 1 && (
            <AccountProfitGraph
              account={account}
              positions={timeFilteredPositions}
              rangeStartTs={rangeStartTs}
              extendToRangeStart={hasTradesBeforeRange}
            />
          )}
          {tabValue === 2 && (
            <AlgorithmBalanceGraph algorithm={algorithmName} />
          )}
        </Grid>
      </Grid>

      <Divider />

      <h3>Open Positions</h3>
      <Grid container>
        <Grid size={{ xs: 12, md: 5 }}>
          {openPositions.length ? (
            openPositions.map((position) => (
              <Position position={position} key={position._id.toString()} />
            ))
          ) : (
            <Typography color="grey" style={{ fontStyle: "italic" }}>
              No open positions
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ pt: 1 }} />

      <h3>Closed Positions</h3>

      <Grid container>
        <Grid size={{ xs: 12, md: 5 }}>
          <FormControlLabel
            label="show cancelled"
            control={
              <Checkbox
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
              />
            }
          />
          {/* <FormControlLabel
            label="show simulated"
            control={
              <Checkbox
                checked={showSimulated}
                onChange={(e) => setShowSimulated(e.target.checked)}
              />
            }
          /> */}

          {positionsToRender.map((position) => (
            <Position position={position} key={position._id.toString()} />
          ))}

          {!showAllPositions && filteredPositions.length > 25 && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={() => setShowAllPositions(true)}
              >
                Show more ({filteredPositions.length - 25})
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  );
}
